import { Link } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "~/components/ui/card";
import { ChevronRight, ChevronLeft } from 'lucide-react';
import FAQs from "../FAQs/FAQs";
import EBookSection from "../Sections/EBookSection";
import { areaSlug } from "~/lib/helpers/slugHelper";
import { DeskSheetRow } from "../Sheet/DeskSheetRow";
import BlogsSection from "../Sections/BlogsSection";
import MobileStatsBar from "../MobileStatsBar/MobileStatsBar";
import convertCurrency from "~/lib/helpers/currencyConverter";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import OfficeSearchBanner from "../common/OfficeBanner";
import MobileHeroBanner from "../ContactPage/MobileHeroBanner";
import StickyCTABanner from "../common/StickyCTABanner";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";

export default function MobileLayout({
  data,
  areaSummary,
  faqs,
  nearbyOffices,
  areaBlogs,
  region,
  workspace,
}: {
  data: any
  areaSummary: any
  faqs?: any
  nearbyOffices?: any
  areaBlogs: any
  region?: any
  workspace?: any
}) {
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const { currency } = useCurrentCurrency();
  const ref = useRef(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const isOverflowing = contentRef.current.scrollHeight > contentRef.current.clientHeight;
      setIsTruncated(isOverflowing);
    }
  }, [areaSummary]);

  return (
    <div>
      <MobileHeroBanner title={areaSummary?.name}
        image={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/area-page/mobile-hero-banner.avif`}
        description={areaSummary?.areaSummary}
        areaPage
      />
      <div className="relative" style={{ marginBottom: 28 }}>
        {/* Stats Card */}
        <div className="px-4 py-2">
          <MobileStatsBar offices={`Upto ${areaSummary?.totalAvailableSpaces}`} users={`Upto ${areaSummary?.maxDesks}`} price={`Avg Price: ${convertCurrency(areaSummary?.deskMonthlyPrice, areaSummary?.currencyIsoCode, currency)}`} AvgPrice={null} />
        </div>

        <div className={`flex `}>
          {/* Left Section */}
          <div
            className={`transition-all duration-300 pt-[10px]  min-w-[375px] w-full `}
          >
            {/* Description Section */}
            <div className="bg-white relative p-4 ">

              {/* commented for now */}

              {/* <h2 className="text-2xl font-semibold my-2">
                Explore {areaSummary?.spaceTypeFormula} Spaces in {areaSummary?.city || areaSummary?.suburb} for Rent with Office Hub!
              </h2> */}

              <div className="relative"
                ref={ref}
                id="heading-container"
              >
                {/* Description Text */}
                <div
                  ref={contentRef}
                  className={`text-oh-black mb-2 text-base relative overflow-hidden heading2 heading3
                    ${expanded ? 'max-h-[2000px]' : 'max-h-28'}
                    transition-[max-height] duration-500 ease-in-out`}
                  dangerouslySetInnerHTML={{ __html: areaSummary?.areaSummary }}
                />

                {/* Gradient Overlay */}
                {!expanded && isTruncated && (
                  <div className="absolute bottom-0 left-0 w-full h-10 bg-gradient-to-b from-transparent to-[#FFFFFF] pointer-events-none"></div>
                )}
              </div>

              {/* Read More Button */}
              {isTruncated && (
                <button
                  className="inline-flex items-center mt-2 text-oh-black underline font-bold text-sm"
                  onClick={() => setExpanded((v) => !v)}
                  type="button"
                >
                  {expanded ? (
                    <>
                      Show less
                      <ChevronLeft size={20} className="ml-1" />
                    </>
                  ) : (
                    <>
                      Read more
                      <ChevronRight size={20} className="ml-1" />
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="flex items-start justify-between w-full">

            </div>
          </div>
        </div>

      </div >

      <div className=" gap-8 px-4 ">
        <div>
          <Card className="-mx-4 rounded-none">
            <CardContent className="p-6 space-y-4">
              {/* area description 1 */}
              <>
                {areaSummary?.description && (
                  <>
                    <h2 id="overview" className="text-2xl font-semibold">
                      Why Choose a{" "}
                      {`${areaSummary?.spaceTypeFormula} in ${areaSummary?.city || areaSummary?.suburb}, ${areaSummary?.state}`}
                    </h2>

                    <div className="relative">
                      {/* Description Text */}
                      <div
                        ref={contentRef}
                        className={`text-gray-800 mb-2 text-base relative overflow-hidden
                           ${expanded ? 'max-h-[2000px]' : 'max-h-40'}
                           transition-[max-height] duration-500 ease-in-out`}
                        dangerouslySetInnerHTML={{
                          __html: areaSummary?.description
                        }}
                      />

                      {/* Gradient Overlay */}
                      {!expanded && isTruncated && (
                        <div className="absolute bottom-0 left-0 w-full h-10 bg-gradient-to-b from-transparent to-[#FFFFFF] pointer-events-none"></div>
                      )}
                    </div>

                    {/* Read More Button */}
                    {isTruncated && (
                      <button
                        className="inline-flex items-center mt-2 text-oh-black underline font-bold text-sm"
                        onClick={() => setExpanded((v) => !v)}
                        type="button"
                      >
                        {expanded ? (
                          <>
                            Show less
                            <ChevronLeft size={20} className="ml-1" />
                          </>
                        ) : (
                          <>
                            Read more
                            <ChevronRight size={20} className="ml-1" />
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
                {/* area description 2 */}

                {areaSummary?.areaDescription2 && (
                  <>
                    <h2 id="overview" className="text-2xl font-semibold">
                      {`Find the Right ${areaSummary?.spaceTypeFormula} in ${areaSummary?.city || areaSummary?.suburb} that Fits Your Unique Needs!`}
                    </h2>

                    <div className="relative">
                      {/* Description Text */}
                      <div
                        ref={contentRef}
                        className={`text-gray-800 mb-2 text-base relative overflow-hidden
                            ${expanded ? 'max-h-[2000px]' : 'max-h-40'}
                            transition-[max-height] duration-500 ease-in-out`}
                        dangerouslySetInnerHTML={{
                          __html: areaSummary?.areaDescription2
                        }}
                      />

                      {/* Gradient Overlay */}
                      {!expanded && isTruncated && (
                        <div className="absolute bottom-0 left-0 w-full h-10 bg-gradient-to-b from-transparent to-[#FFFFFF] pointer-events-none"></div>
                      )}
                    </div>

                    {/* Read More Button */}
                    {isTruncated && (
                      <button
                        className="inline-flex items-center mt-2 text-oh-black underline font-bold text-sm"
                        onClick={() => setExpanded((v) => !v)}
                        type="button"
                      >
                        {expanded ? (
                          <>
                            Show less
                            <ChevronLeft size={20} className="ml-1" />
                          </>
                        ) : (
                          <>
                            Read more
                            <ChevronRight size={20} className="ml-1" />
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}

              </>
            </CardContent>
          </Card>

          {(() => {
            const offices = Array.isArray(nearbyOffices) ? nearbyOffices : [];
            const visibleOffices = showAll ? offices : offices.slice(0, 4);

            return (
              <>
                {offices.length ? (
                  <div id={PAGE_SECTION_IDS.nearby} className={`py-6 ${PAGE_SECTION_SCROLL_CLASS}`}>
                    <h2 className="text-2xl font-semibold mb-2">
                      Compare Average Desk Prices by Area and Team Size in {areaSummary?.city || areaSummary?.suburb}
                    </h2>
                    <p className="text-md text-gray-600 mb-6">
                      Use this guide to estimate your monthly office expenses by suburb and team size.
                    </p>
                    <div className="-mx-4">
                      <div className="overflow-x-auto w-full scrollbar-hide">
                        <div className="min-w-[1040px]">
                          {/* Header */}
                          <div className="flex bg-white font-medium text-sm py-5 border-t-2 border-b-2 border-gray-200">
                            {/* Sticky Location Column */}
                            <div className="sticky left-0 bg-white text-center font-semibold min-w-40 w-40 max-w-40 pl-5 text-lg overflow-hidden whitespace-nowrap text-ellipsis">
                              Location
                            </div>

                            {/* Scrollable Price rows */}
                            <div className="flex text-lg [&>div]:font-semibold [&>div]:min-w-40 [&>div]:w-40 [&>div]:max-w-40 [&>div]:px-2 [&>div]:text-center [&>div]:overflow-hidden [&>div]:whitespace-nowrap [&>div]:text-ellipsis">
                              <div>1-5 Desks</div>
                              <div>6-10 Desks</div>
                              <div>11-15 Desks</div>
                              <div>16-25 Desks</div>
                              <div>26-50 Desks</div>
                            </div>
                          </div>

                          {/* Rows */}
                          {offices.map((row: any, index: number) => (
                            <DeskSheetRow
                              key={index}
                              row={row}
                              index={index}
                              {...({ areaSummary } as any)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {visibleOffices.length ? (
                  <section className="mb-12 mt-2">
                    <h2 className="text-base font-semibold mb-1">
                      Explore more offices near {areaSummary?.city || areaSummary?.suburb}
                    </h2>
                    <p className="text-muted-foreground mb-5 text-oh-black">
                      Here are major business districts in {areaSummary?.city || areaSummary?.suburb} where office spaces are in demand:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 rounded-lg overflow-hidden border border-gray-200">
                      {visibleOffices.map((hub: any, i: number) => {
                        const bgClass = i % 2 === 0 ? 'bg-gray-50' : 'bg-white';

                        return (
                          <Link
                            key={i}
                            to={areaSlug(hub)}
                            target="_blank"
                            className={`${bgClass} p-4 transition h-40 border border-gray-200 border-t-0`}
                          >
                            <h3 className="font-medium text-lg">{hub.suburb}</h3>
                            <div
                              className="text-sm text-muted-foreground mt-1"
                              dangerouslySetInnerHTML={{
                                __html: `<p style="height:60px;overflow:hidden; text-overflow:ellipsis">${hub.areaDescription}</p>`,
                              }}
                            ></div>
                            <p className="text-sm font-semibold text-gray-600 mt-2">&lt;read more&gt;</p>
                          </Link>
                        );
                      })}
                    </div>

                    {offices.length > 3 && (
                      <div className="text-center mt-4">
                        <button
                          onClick={() => setShowAll(!showAll)}
                          className="text-oh-black text-md w-full font-semibold px-4 py-3 border border-gray-300 rounded-md bg-white"
                          type="button"
                        >
                          {showAll ? "Show less" : "Show more"}
                        </button>
                      </div>
                    )}
                  </section>
                ) : null}
              </>
            );
          })()}

          {faqs?.length ? (
            <FAQs
              title={`Answers to Your Questions Related to ${areaSummary.spaceTypeFormula} in ${areaSummary?.city || areaSummary?.suburb}`}
              faqs={faqs}
            />
          ) : null}
          {areaSummary?.blogPosts.length ?
            <EBookSection areaSummary={areaSummary} /> :
            null
          }
        </div>
      </div>

      {areaBlogs?.length ? (
        <div className="px-4">
          <BlogsSection areaBlogs={areaBlogs} heading={`Latest Blogs & Insights`} region={region} country={region} />
        </div>
      ) : null}

      <OfficeSearchBanner country={data.params.country} />
      <StickyCTABanner
        data={workspace}
        region={region}
      />
    </div >
  );
}
